import { EscHotKeyIcon } from '@modules/common/components/HotKeyIcons';
import { CmdHotKeyIcon } from '@modules/common/components/HotKeyIcons/CmdHotKeyIcon';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { useHotkey } from '@modules/common/hooks';
import { DashboardDescriptionInput } from '@modules/dashboard/components/DashboardDescriptionInput';
import { DashboardNameAndEmojiInput } from '@modules/dashboard/components/DashboardNameAndEmojiInput';
import {
  DASHBOARD_DESCRIPTION_MAX_LENGTH,
  DASHBOARD_NAME_MAX_LENGTH,
} from '@modules/dashboard/constants';
import {
  getEmojiFromDashboard,
  getShortMetadataForDashboard,
  trackDashboardEvent,
} from '@modules/dashboard/helpers';
import { useUpsertDashboard } from '@modules/dashboard/hooks';
import { KeyboardReturnRounded } from '@mui/icons-material';
import CloseIcon from '@mui/icons-material/Close';
import { LoadingButton } from '@mui/lab';
import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  Typography,
} from '@mui/material';
import { DashboardViewFragment } from 'generated/graphql';
import { useState } from 'react';

interface EditDashboardDetailsDialogContentProps {
  dashboard: DashboardViewFragment;
}

export const EditDashboardDetailsDialogContent = ({
  dashboard,
}: EditDashboardDetailsDialogContentProps) => {
  const { closeModal } = useMuiModalContext();
  const { showErrorNotification } = useNotificationContext();

  const [dialogRefInState, setDialogRefInState] =
    useState<HTMLDivElement | null>(null);

  const [name, setName] = useState(dashboard.title);
  const [description, setDescription] = useState(dashboard.description);
  const [emoji, setEmoji] = useState(getEmojiFromDashboard(dashboard));

  const shortMetadata = getShortMetadataForDashboard({ dashboardEmoji: emoji });

  const { upsertDashboard, loading } = useUpsertDashboard();

  const onClose = () => closeModal();

  const onSave = async () => {
    onClose();
    try {
      await upsertDashboard({
        dashboardProps: {
          title: name,
          description,
          shortMetadata,
        },
        shouldRefetchDashboard: false,
      });
      trackDashboardEvent({
        eventName: 'Dashboard Details Updated',
        dashboardLink: window.location.href,
        updates: {
          name,
          description,
          emoji,
        },
      });
    } catch {
      showErrorNotification({
        contentComponent: 'Failed to update Dashboard details',
        props: {},
      });
    }
  };

  const isNameEmpty = name.trim().length === 0;

  const hasDetailsChanged =
    name !== dashboard.title ||
    description !== dashboard.description ||
    shortMetadata !== dashboard.shortMetadata;

  const isNameOverflowing = name.length > DASHBOARD_NAME_MAX_LENGTH;

  const isDescriptionOverflowing =
    description.length > DASHBOARD_DESCRIPTION_MAX_LENGTH;

  const isSaveDisabled =
    isNameEmpty ||
    isDescriptionOverflowing ||
    isNameOverflowing ||
    !hasDetailsChanged;

  useHotkey({
    hotkey: '$mod+enter',
    onHotkey: onSave,
    isHotKeyDisabled: isSaveDisabled,
  });

  return (
    <Box
      ref={setDialogRefInState}
      component="form"
      onSubmit={(event) => {
        event.preventDefault();
        onSave();
      }}
    >
      <DialogTitle>
        <Stack direction="row" justifyContent="space-between">
          <Typography variant="h6">Edit Dashboard</Typography>
          <IconButton aria-label="close" onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <Divider />
      <DialogContent>
        <Stack spacing={2}>
          <DashboardNameAndEmojiInput
            emoji={emoji}
            onEmojiChange={setEmoji}
            name={name}
            onNameChange={setName}
            dialogRef={dialogRefInState}
          />
          <DashboardDescriptionInput
            description={description}
            onDescriptionChange={setDescription}
          />
        </Stack>
      </DialogContent>
      <Divider />
      <DialogActions>
        <Stack direction="row" m={1} spacing={2}>
          <Button
            onClick={onClose}
            variant="outlined"
            endIcon={<EscHotKeyIcon />}
          >
            Cancel
          </Button>
          <LoadingButton
            disabled={isSaveDisabled}
            loading={loading}
            variant="contained"
            type="submit"
            endIcon={
              <CmdHotKeyIcon
                plusHotKeyNode={<KeyboardReturnRounded />}
                isDisabled={isSaveDisabled}
              />
            }
          >
            Save
          </LoadingButton>
        </Stack>
      </DialogActions>
    </Box>
  );
};
